import { ProcessGeneral } from 'api/endpoints/processes';
import { UPLOAD_FILE_FIELD_NAME } from 'constants/global';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponse, ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequestTyped } from 'container/search';

import {
  DriverSettlementsBatchWithErrorGroup,
  DriverSettlementsBatchWithHypermedia,
  IDriverSettlementsBatchRepo,
  SearcheableDriverSettlementsBatch,
  SearcheableDriverSettlementsBatchWithError,
} from './IDriverSettlementsBatchRepo';

export class DriverSettlementsBatchRepo
  extends BaseRepo
  implements IDriverSettlementsBatchRepo
{
  search = async (
    draft: ProcessGeneral,
    request: SearchRequestTyped<SearcheableDriverSettlementsBatch>
  ): ApiResponseAsync<Array<DriverSettlementsBatchWithHypermedia>> => {
    const link = draft._links?.['searchDriverSettlementsBatches'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "search Driver Settlements batches" is not available')
      );
    }

    const searchResult = await this.helpers.executeSearch<
      DriverSettlementsBatchWithHypermedia[]
    >(link, request, { convertRequestToSnakeCase: true });
    if (isApiError(searchResult)) {
      return searchResult;
    }
    return searchResult ?? [];
  };

  searchBatchesWithError = async (
    draft: ProcessGeneral,
    request: SearchRequestTyped<SearcheableDriverSettlementsBatchWithError>
  ): ApiResponseAsync<Array<DriverSettlementsBatchWithErrorGroup>> => {
    const link = draft._links?.['searchDriverSettlementsBatchesWithErrors'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "search Driver Settlements batches with error" is not available'
        )
      );
    }

    const searchResult = await this.helpers.executeSearch<
      DriverSettlementsBatchWithErrorGroup[]
    >(link, request);
    if (isApiError(searchResult)) {
      return searchResult;
    }
    return searchResult ?? [];
  };

  uploadFile = async (
    draft: ProcessGeneral,
    file: File,
    payload?: Record<string, any>
  ): ApiResponseAsync<void> => {
    const link = draft._links?.['uploadDriverSettlementsBatch'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "uploadDriverSettlementsBatch" is not available in process draft'
        )
      );
    }

    const fileFormData = new FormData();
    fileFormData.append(UPLOAD_FILE_FIELD_NAME, file);

    if (payload) fileFormData.append('properties', JSON.stringify(payload));

    return await this.helpers.handlePost(link.href, fileFormData);
  };

  reProcessErrorBatch = async (
    batch: DriverSettlementsBatchWithHypermedia
  ): ApiResponseAsync<DriverSettlementsBatchWithHypermedia> => {
    const link = batch._links?.['reprocessError'];
    if (!link)
      return buildBadRequestApiError(
        new Error('link "reprocess error batch" is not available')
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  delete = (
    batch: DriverSettlementsBatchWithHypermedia
  ): ApiResponseAsync<void> => {
    const link = batch._links?.['delete'];
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  getBatchWithErrorFileLink = (
    batch: DriverSettlementsBatchWithHypermedia
  ): ApiResponse<Link> => {
    const link = batch._links?.['openErrorFile'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "open error file" is not available for the Driver Settlements batch'
        )
      );
    }

    return link;
  };

  getBatchSourceFileLink = (
    batch: DriverSettlementsBatchWithHypermedia
  ): ApiResponse<Link> => {
    const link = batch._links?.['openFile'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "open source file" is not available for the Driver Settlements batch'
        )
      );
    }

    return link;
  };
}
